import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/api';
import {
  ScanLine, FileText, ArrowRight, Download,
  CheckCircle2, RefreshCw, AlertTriangle, Clock
} from 'lucide-react';

const statusConfig = {
  created: { color: 'bg-muted/50 text-muted-foreground border-border', label: 'Created' },
  uploaded: { color: 'bg-muted/50 text-muted-foreground border-border', label: 'Uploaded' },
  processing: { color: 'bg-blue-50 dark:bg-blue-950/50 text-blue-400 border-blue-900', label: 'Processing' },
  review: { color: 'bg-amber-50 dark:bg-amber-950/30 text-primary border-primary/30', label: 'Ready for Review' },
  completed: { color: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900', label: 'Completed' },
  synced: { color: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900', label: 'Synced' },
  failed: { color: 'bg-red-50 dark:bg-red-950/50 text-red-400 border-red-900', label: 'Failed' },
};

export default function DigitizeDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/digitize/stats');
        setStats(res.data);
      } catch (err) {
        console.error('Failed to load digitize stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const isEmpty = !stats || stats.total_projects === 0;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-[1200px]" data-testid="digitize-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <ScanLine className="w-6 h-6 text-primary" />
          <div>
            <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground">Digitization Projects</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Paper-to-digital drawing conversion</p>
          </div>
        </div>
        <Link
          to="/digitize/upload"
          data-testid="new-digitize-btn"
          className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-10 px-6 transition-colors"
        >
          <ScanLine className="w-4 h-4" />
          New Digitization
        </Link>
      </div>

      {/* Stats */}
      {!isEmpty && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 stagger-children">
          <div className="bg-card border border-border rounded-sm p-5" data-testid="stat-total">
            <p className="font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground">Total Projects</p>
            <p className="font-mono text-2xl font-bold text-foreground mt-0.5">{stats.total_projects}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-5" data-testid="stat-processing">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <p className="font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground">Processing</p>
            </div>
            <p className="font-mono text-2xl font-bold text-blue-400">{stats.processing}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-5" data-testid="stat-completed">
            <div className="flex items-center gap-2 mb-0.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              <p className="font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground">Completed</p>
            </div>
            <p className="font-mono text-2xl font-bold text-emerald-400">{stats.completed}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-5" data-testid="stat-failed">
            <div className="flex items-center gap-2 mb-0.5">
              <AlertTriangle className="w-3 h-3 text-red-400" />
              <p className="font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground">Failed</p>
            </div>
            <p className="font-mono text-2xl font-bold text-red-400">{stats.failed}</p>
          </div>
        </div>
      )}

      {/* Projects List */}
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-muted border border-border flex items-center justify-center mb-6">
            <ScanLine className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="font-heading font-bold text-lg uppercase tracking-tight text-foreground/80" data-testid="empty-state-title">No Digitization Projects Yet</h2>
          <p className="text-muted-foreground text-sm mt-2 max-w-md mb-6">
            Upload your first set of scanned paper drawings to start the digitization process.
          </p>
          <Link
            to="/digitize/upload"
            data-testid="empty-start-btn"
            className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-11 px-8 transition-colors"
          >
            <ScanLine className="w-4 h-4" />
            Start Digitizing
          </Link>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm" data-testid="projects-list">
          <div className="grid grid-cols-[1fr_120px_100px_80px_100px] gap-4 px-5 py-3 border-b border-border text-[9px] font-heading uppercase tracking-widest text-muted-foreground">
            <span>Project</span>
            <span>Status</span>
            <span>Sheets</span>
            <span>Rooms</span>
            <span>Actions</span>
          </div>
          <div className="divide-y divide-slate-800/50">
            {(stats.recent_projects || []).map((project) => {
              const sc = statusConfig[project.status] || statusConfig.created;
              return (
                <div key={project.id} className="grid grid-cols-[1fr_120px_100px_80px_100px] gap-4 px-5 py-3.5 items-center hover:bg-secondary/20 transition-colors" data-testid={`project-row-${project.id}`}>
                  <div className="min-w-0">
                    <Link to={`/jobs/${project.id}`} className="font-mono text-sm text-foreground hover:text-primary transition-colors truncate block">
                      {project.project_name || 'Untitled'}
                    </Link>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                      {new Date(project.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium font-mono uppercase tracking-wide border w-fit ${sc.color}`}>
                    {sc.label}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">{project.sheet_count}</span>
                  <span className="font-mono text-xs text-muted-foreground">{project.space_count}</span>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/jobs/${project.id}`}
                      className="p-1.5 hover:bg-secondary rounded-sm text-muted-foreground hover:text-foreground transition-colors"
                      title="View Details"
                      data-testid={`view-project-${project.id}`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </Link>
                    {(project.status === 'review' || project.status === 'completed' || project.status === 'synced') && (
                      <Link
                        to={`/jobs/${project.id}`}
                        className="p-1.5 hover:bg-secondary rounded-sm text-muted-foreground hover:text-emerald-400 transition-colors"
                        title="Export"
                        data-testid={`export-project-${project.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Link>
                    )}
                    {project.status === 'processing' && (
                      <Clock className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
